// In-browser alignment on the "align" volumes: gathers in-box points (CPU copies),
// runs ICP rounds a cloud at a time between frames, keeps per-cloud transforms
// (localStorage per dataset), undo/reset, and exports placements for the VPS.
import type { Viewer, Cloud } from './viewer.ts';
import { download } from './download.ts';
import type { V3 } from './math.ts';
import { proc } from './procs.ts';
import * as cache from './cache.ts';
import { decodeRecord } from './format.ts';
import { type Volume, inBoxes } from './volumes.ts';
import { type Rigid, type FitResult, identity, compose, apply, yawDeg, rows4, parseRigid, isIdentity, transformPoints, Grid, fitSteps } from './align.ts';

const MARGIN = 0.3;        // target boxes are this much bigger on every side (as the script)
const COLLECT = 0.8;       // gather this far round the boxes, so moved clouds still have points
const FINEST = 0.02;       // fetch levels down to the ~2 cm one
const CAP = 120000;        // in-box points kept per cloud (targets)
const SRC_CAP = 15000;     // points of the moving cloud used per fit (plenty for 4 motions)
const SLICE = 12;          // ms of fitting per frame

interface Pts { xyz: Float32Array; nrm: Float32Array }
interface Stat { rms0: number; rms1: number; points: number; boxes: number; free: string; note?: string }

const grow = (v: Volume, m: number): Volume => ({ ...v, size: v.size.map(s => s + 2 * m) as V3 });
const applies = (v: Volume, id: string) => !v.clouds.length || v.clouds.includes(id);

const pick = (a: Float32Array, m: Uint8Array) => {
  let n = 0;
  for (const x of m) n += x;
  const out = new Float32Array(3 * n);
  for (let i = 0, j = 0; i < m.length; i++) if (m[i]) { out[j++] = a[3 * i]; out[j++] = a[3 * i + 1]; out[j++] = a[3 * i + 2]; }
  return out;
};
/** Every k-th point so at most cap remain. */
function thin(a: Float32Array, cap: number) {
  const n = a.length / 3;
  if (n <= cap) return a;
  const out = new Float32Array(3 * cap), k = n / cap;
  for (let i = 0; i < cap; i++) out.set(a.subarray(3 * Math.floor(i * k), 3 * Math.floor(i * k) + 3), 3 * i);
  return out;
}
const frame = () => new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));
const AUTO_FINEST = 0.03;   // Auto-Align: levels down to ~3 cm of every chunk
const AUTO_CAP = 60000;     // and at most this many points per cloud

/** Points (with normals) of some chunks of a cloud, levels 0..nlev-1, in its file coords; mb fetched. */
async function readChunks(c: Cloud, chunks: Cloud['chunks'], nlev: number, say: (lev: number, mb: number) => void) {
  const xyz: number[] = [], nrm: number[] = [];
  let fetched = 0;
  for (let lev = 0; lev < nlev; lev++) {
    // contiguous runs of these chunks' level in the level-major file
    const parts = chunks.filter(ch => ch.info.levelCounts[lev]).map(ch => ({ ch, a: ch.info.offsets[lev], b: ch.info.offsets[lev] + ch.info.levelCounts[lev] * c.idx.record }))
      .sort((p, q) => p.a - q.a);
    const runs: (typeof parts)[] = [];
    for (const p of parts) { const r = runs[runs.length - 1]; if (r && r[r.length - 1].b === p.a) r.push(p); else runs.push([p]); }
    await Promise.all(runs.map(async run => {
      // each chunk level from the browser cache, or fetched (and kept), like the viewer's
      const { bufs, net } = await cache.fetchParts(c.bin, run.map(p => ({ offset: p.a, bytes: p.b - p.a })));
      fetched += net;
      for (const [k, p] of run.entries()) {
        const dv = new DataView(bufs[k]), n = (p.b - p.a) / c.idx.record;
        for (let i = 0; i < n; i++) {
          const pt = decodeRecord(dv, i, p.ch.info, c.idx.record);
          if (!pt.n) continue;
          xyz.push(...pt.p); nrm.push(...pt.n);
        }
      }
    }));
    say(lev, fetched / 1e6);
  }
  return { P: new Float32Array(xyz), N: new Float32Array(nrm), mb: fetched / 1e6 };
}

/** Every k-th point (and its normal) so at most cap remain. */
function thinPN(p: Pts, cap: number): Pts {
  const n = p.xyz.length / 3;
  if (n <= cap) return p;
  const P = new Float32Array(3 * cap), N = new Float32Array(3 * cap), k = n / cap;
  for (let i = 0; i < cap; i++) { const j = 3 * Math.floor(i * k); P.set(p.xyz.subarray(j, j + 3), 3 * i); N.set(p.nrm.subarray(j, j + 3), 3 * i); }
  return { xyz: P, nrm: N };
}

/** The Align tool's placements kept in this browser for a dataset (by cloud id), on top of each cloud's published T. */
export function storedAlignment(base: string): Map<string, Rigid> {
  const m = new Map<string, Rigid>();
  try {
    const o = JSON.parse(localStorage.getItem('cloudview.align:' + base) || '{}');
    for (const [id, x] of Object.entries(o)) { const r = parseRigid(x); if (r) m.set(id, r); }
  } catch { /* storage blocked or junk */ }
  return m;
}

export class AlignTool {
  T = new Map<string, Rigid>();
  stats = new Map<string, Stat>();
  busy = false;
  private undo: Map<string, Rigid> | null = null;
  private data: Map<string, Pts> | null = null;
  private dataKey = '';
  private storeKey: string;

  constructor(private v: Viewer, private base: string, private $: (id: string) => HTMLElement) {
    this.storeKey = 'cloudview.align:' + base;
    $('arun').onclick = () => this.run(Math.max(1, Math.min(200, parseInt(($('aiters') as HTMLInputElement).value) || 10)));
    $('aundo').onclick = () => this.restore();
    $('areset').onclick = () => { this.undo = new Map(this.T); this.replace(new Map()); this.stats.clear(); this.saved(); };
    $('aexport').onclick = () => this.exportFile();
    $('asave').onclick = () => { void this.save(); };
    $('aauto').onclick = () => { void this.auto(Math.max(1, Math.min(200, parseInt(($('aiters') as HTMLInputElement).value) || 10))); };
  }

  /**
   * In the editor: a scan's points as the editor shows them (its alignment-tree moves too, published or
   * not), less this tool's own T, i.e. in the frame this tool's T applies to. null: use the published cloud.
   */
  pointsOf: ((id: string) => Pts | null) | null = null;

  /**
   * Auto-Align: like Align, but with no boxes: each shown cloud (but the reference) is fitted onto all
   * the others wherever they overlap, from where they are now (so it refines a placement made by hand,
   * and doesn't search for one). Turn about the vertical, x, y, and z with Free height. One round per press.
   */
  async auto(iters: number) {
    if (this.busy) return;
    const out = this.$('aout'), say = (s: string) => { out.textContent = s; };
    const shown = this.fitted().filter(c => c.visible);
    if (shown.length < 2) { say('Auto-Align needs at least two shown clouds.'); return; }
    this.busy = true;
    this.$('aauto').textContent = 'Auto-aligning…';
    const t0 = performance.now(), before = new Map(this.T), head = this.begin('Auto-Align');
    try {
      // points of every shown cloud: from the editor if it has the scan, else ~3 cm levels of the published cloud
      const sp = this.v.scene.spacing, nlev = Math.max(1, sp.findIndex(s => s <= AUTO_FINEST + 1e-9) + 1 || sp.length);
      const data = new Map<string, Pts>();
      for (const c of shown) {
        const p = this.pointsOf?.(c.meta.id) ?? null;
        if (p) { data.set(c.meta.id, thinPN(p, AUTO_CAP)); continue; }
        const got = await readChunks(c, c.chunks, nlev, (lev, mb) => say(`gathering points: ${c.meta.name}, level ${lev + 1}/${nlev} (${mb.toFixed(1)} MB)`));
        this.check();
        data.set(c.meta.id, thinPN({ xyz: got.P, nrm: got.N }, AUTO_CAP));
      }
      const freeZ = (this.$('afreez') as HTMLInputElement).checked;
      const anchor = shown.includes(this.ref) ? this.ref : shown[0];
      const world = (c: Cloud) => { const d = data.get(c.meta.id)!, T = this.get(c); return { xyz: transformPoints(T, d.xyz), nrm: transformPoints(T, d.nrm, undefined, false) }; };
      let k = 0;
      for (const c of shown) {
        if (c === anchor) continue;
        head.set(c.meta.name, k++ / (shown.length - 1));
        const id = c.meta.id, w = world(c), n = w.xyz.length / 3;
        if (n < 200) { this.stats.set(id, { rms0: NaN, rms1: NaN, points: n, boxes: 0, free: '', note: `only ${n} points` }); continue; }
        const Q: Float32Array[] = [], QN: Float32Array[] = [];
        for (const k of shown) if (k !== c) { const wk = world(k); Q.push(wk.xyz); QN.push(wk.nrm); }
        const cat = (a: Float32Array[]) => { const o = new Float32Array(a.reduce((s, x) => s + x.length, 0)); let i = 0; for (const x of a) { o.set(x, i); i += x.length; } return o; };
        say(`auto-aligning ${c.meta.name}…`);
        await this.frame();
        const grid = new Grid(cat(Q));
        await this.frame();
        const r = await this.fit(fitSteps(thin(w.xyz, SRC_CAP), grid, cat(QN), { iters, canTurn: true, freeZ }));
        if (r.accepted) this.set(c, compose(r.step, this.get(c)));
        this.stats.set(id, { rms0: r.rms0, rms1: r.rms1, points: n, boxes: 0, free: r.free.join(' ') || 'nothing',
          note: r.accepted ? 'auto, whole overlap' : (Number.isFinite(r.rms0) ? 'no better: left as it was' : 'no overlap with the others') });
        this.v.dirty = true;
        await this.frame();
      }
      this.undo = before;
      this.lastMs = performance.now() - t0;
      this.saved();
    } catch (e) {
      if (this.stopped) this.cancelled(before, 'Auto-Align'); else say('Auto-Align failed: ' + (e as Error).message);
    } finally {
      this.busy = false;
      head.end();
      this.$('aauto').textContent = 'Auto-Align';
    }
  }

  /** In the editor: write the scans' alignments into the scene (as edits), then drop them here. */
  onSave: ((moved: Map<string, Rigid>) => Promise<string[]>) | null = null;
  private async save() {
    if (!this.onSave || this.busy) return;
    const moved = new Map([...this.T].filter(([id, r]) => !isIdentity(r) && id !== this.ref.meta.id
      && this.v.clouds.find(c => c.meta.id === id)?.meta.kind !== 'object'));
    if (!moved.size) { this.$('aout').textContent = 'Nothing to save: no scan has been moved.'; return; }
    this.busy = true;
    try {
      const done = await this.onSave(moved);
      if (!done.length) return;
      this.undo = null;
      const T = new Map(this.T);
      for (const id of done) T.delete(id);
      this.replace(T);
      this.saved();
      this.$('aout').textContent += `\nsaved to the scene: ${done.length} scan${done.length === 1 ? '' : 's'} (Undo in Edits takes it back)`;
    } finally { this.busy = false; }
  }

  get ref() { return this.v.clouds[0]; }   // the scene's first cloud stays fixed
  /** Clouds that take part in alignment: not movable objects (they move on their own). */
  private fitted() { return this.v.clouds.filter(c => c.meta.kind !== 'object'); }
  private alignVols() { return this.v.s.volumes.filter(v => v.on && v.kind === 'align'); }

  load() {
    const o = storedAlignment(this.base);
    for (const c of this.v.clouds) { const r = o.get(c.meta.id); if (r) this.T.set(c.meta.id, r); }
    for (const c of this.v.clouds) this.v.setTransform(c, this.get(c));
    this.show();
  }

  set(c: Cloud, r: Rigid) {
    this.T.set(c.meta.id, r);
    this.v.setTransform(c, r);
    // split parts and objects of a scan move with it unless aligned on their own
    for (const k of this.v.clouds) if (k.meta.parent === c.meta.id && !this.T.has(k.meta.id)) this.v.setTransform(k, r);
  }
  /** All alignments at once (undo, reset). */
  private replace(T: Map<string, Rigid>) {
    this.T = new Map(T);
    for (const c of this.v.clouds) this.v.setTransform(c, this.get(c));
  }
  /** A cloud's alignment: its own, else its parent scan's. */
  get(c: Cloud): Rigid {
    const own = this.T.get(c.meta.id);
    if (own) return own;
    return (c.meta.parent && this.T.get(c.meta.parent)) || identity();
  }

  private saved() {
    try {
      const o: Record<string, Rigid> = {};
      for (const [k, r] of this.T) if (!isIdentity(r)) o[k] = r;
      localStorage.setItem(this.storeKey, JSON.stringify(o));
    } catch { /* storage blocked */ }
    this.show();
  }

  private restore() {
    if (!this.undo || this.busy) return;
    this.replace(this.undo);
    this.undo = null;
    this.stats.clear();
    this.saved();
  }

  /** Fetch levels down to ~2 cm of every chunk near an align box; keep in-box points (file coords). */
  async gather(say: (s: string) => void) {
    const vols = this.alignVols(), key = JSON.stringify(vols.map(v => [v.center, v.size, v.ypr]));
    if (this.data && key === this.dataKey) return this.data;
    const v = this.v, sp = v.scene.spacing, nlev = Math.max(1, sp.findIndex(s => s <= FINEST + 1e-9) + 1 || sp.length);
    const data = new Map<string, Pts>();
    let fetched = 0;
    for (const c of this.fitted()) {
      const T = this.get(c), boxes = vols.map(b => grow(b, COLLECT));
      const near = c.chunks.filter(ch => {
        const w = apply(T, ch.center);
        return boxes.some(b => inBoxes(new Float32Array(w), [grow(b, ch.radius)])[0]);
      });
      const got = await readChunks(c, near, nlev, (lev, mb) => say(`gathering points: ${c.meta.name}, level ${lev + 1}/${nlev} (${(fetched + mb).toFixed(1)} MB)`));
      fetched += got.mb;
      let P = got.P, N = got.N;
      const m = inBoxes(transformPoints(T, P), boxes);
      P = pick(P, m); N = pick(N, m);
      const n = P.length / 3;
      if (n > CAP) {           // even thinning: every k-th point
        const k = n / CAP, P2 = new Float32Array(3 * CAP), N2 = new Float32Array(3 * CAP);
        for (let i = 0; i < CAP; i++) { const j = 3 * Math.floor(i * k); P2.set(P.subarray(j, j + 3), 3 * i); N2.set(N.subarray(j, j + 3), 3 * i); }
        P = P2; N = N2;
      }
      data.set(c.meta.id, { xyz: P, nrm: N });
      await this.frame();
    }
    this.data = data;
    this.dataKey = key;
    return data;
  }

  /** One round: each cloud but the reference fitted against the others, N ICP iterations each. */
  async run(iters: number) {
    if (this.busy) return;
    const vols = this.alignVols(), out = this.$('aout');
    if (!vols.length) { out.textContent = 'No enabled Align boxes.'; return; }
    this.busy = true;
    this.$('arun').textContent = 'Aligning…';
    const say = (s: string) => { out.textContent = s; };
    const t0 = performance.now(), before = new Map(this.T), head = this.begin('Align');
    try {
      const data = await this.gather(say);
      const freeZ = (this.$('afreez') as HTMLInputElement).checked;
      const world = (c: Cloud) => {
        const d = data.get(c.meta.id)!, T = this.get(c);
        return { xyz: transformPoints(T, d.xyz), nrm: transformPoints(T, d.nrm, undefined, false) };
      };
      const all = this.fitted();
      for (const c of all) {
        if (c === this.ref) continue;
        head.set(c.meta.name, all.indexOf(c) / all.length);
        const id = c.meta.id, mine = vols.filter(b => applies(b, id));
        const w = world(c), m = inBoxes(w.xyz, mine), src = pick(w.xyz, m), n = src.length / 3;
        const skip = (note: string) => this.stats.set(id, { rms0: NaN, rms1: NaN, points: n, boxes: 0, free: '', note });
        if (!mine.length) { skip('no align box applies'); continue; }
        if (n < 200) { skip(`only ${n} points in its boxes`); continue; }
        const grown = mine.map(b => grow(b, MARGIN)), Q: number[] = [], QN: number[] = [];
        for (const k of this.fitted()) {
          if (k === c) continue;
          await this.frame();
          const wk = world(k), mk = inBoxes(wk.xyz, grown);
          for (let i = 0; i < mk.length; i++) if (mk[i]) { Q.push(wk.xyz[3 * i], wk.xyz[3 * i + 1], wk.xyz[3 * i + 2]); QN.push(wk.nrm[3 * i], wk.nrm[3 * i + 1], wk.nrm[3 * i + 2]); }
        }
        if (Q.length < 600) { skip('no other cloud has points in its boxes'); continue; }
        const boxes = mine.filter(b => { let k = 0; for (const x of inBoxes(src, [b])) k += x; return k >= 100; }).length;
        say(`aligning ${c.meta.name}…`);
        await this.frame();
        const grid = new Grid(new Float32Array(Q));
        await this.frame();
        const r = await this.fit(fitSteps(thin(src, SRC_CAP), grid, new Float32Array(QN), { iters, canTurn: boxes >= 2, freeZ }));
        if (r.accepted) this.set(c, compose(r.step, this.get(c)));
        this.stats.set(id, {
          rms0: r.rms0, rms1: r.rms1, points: n, boxes,
          free: r.free.join(' ') || 'nothing', note: r.accepted ? undefined : 'step rejected (rms got worse)',
        });
        this.v.dirty = true;
        await this.frame();
      }
      this.undo = before;
      this.lastMs = performance.now() - t0;
      this.saved();
    } catch (e) {
      if (this.stopped) this.cancelled(before, 'Align'); else say('Align failed: ' + (e as Error).message);
    } finally {
      this.busy = false;
      head.end();
      this.$('arun').textContent = 'Align';
    }
  }
  lastMs = 0;

  // ── Cancel (in the status bar): checked at each frame the fit yields; the clouds go back to where they were ──
  private stopped = false;
  private statsBefore = new Map<string, Stat>();
  private begin(title: string) {
    this.stopped = false;
    this.statsBefore = new Map(this.stats);
    return proc(title, { cancel: () => { this.stopped = true; } });
  }
  private check() { if (this.stopped) throw new Error('cancelled'); }
  private async frame() { await frame(); this.check(); }
  /** Run a fit's steps to the end, a slice per frame. */
  private async fit(g: ReturnType<typeof fitSteps>): Promise<FitResult> {
    let ts = performance.now();
    for (;;) {
      const s = g.next();
      if (s.done) return s.value;
      if (performance.now() - ts > SLICE) { await this.frame(); ts = performance.now(); }
    }
  }
  private cancelled(before: Map<string, Rigid>, what: string) {
    this.replace(before);
    this.stats = this.statsBefore;
    this.show();
    this.v.dirty = true;
    const out = this.$('aout');
    out.textContent = `${what} cancelled: the clouds are back where they were\n${out.textContent}`;
  }

  /** Centroid of the cloud's in-box points, for reporting moves. */
  private centroid(c: Cloud): V3 {
    const d = this.data?.get(c.meta.id);
    if (!d || !d.xyz.length) { const b = c.meta.bbox; return [(b[0] + b[3]) / 2, (b[1] + b[4]) / 2, (b[2] + b[5]) / 2]; }
    const s: V3 = [0, 0, 0], n = d.xyz.length / 3;
    for (let i = 0; i < n; i++) for (let k = 0; k < 3; k++) s[k] += d.xyz[3 * i + k] / n;
    return s;
  }
  move(c: Cloud): V3 { const p = this.centroid(c), q = apply(this.get(c), p); return [q[0] - p[0], q[1] - p[1], q[2] - p[2]]; }

  show() {
    const lines: string[] = [];
    for (const c of this.v.clouds) {
      const T = this.get(c), st = this.stats.get(c.meta.id), name = c.meta.name.replace(/^RTAB /, '').replace(/ \(.*\)$/, '');
      if (c === this.ref) { lines.push(`${name}: reference, fixed`); continue; }
      if (c.meta.kind === 'object') { lines.push(`${name}: movable object, not aligned (moves with its scan)`); continue; }
      const mv = this.move(c).map(x => (x * 100).toFixed(1)).join(' ');
      let s = `${name}: `;
      if (st?.note && !Number.isFinite(st.rms1)) s += st.note;
      else if (st) s += `rms ${(st.rms0 * 100).toFixed(1)} → ${(st.rms1 * 100).toFixed(1)} cm, ${st.note?.startsWith('auto') || st.note?.startsWith('no ') ? 'whole overlap' : `${st.boxes} box${st.boxes === 1 ? '' : 'es'}`}, moves: ${st.free}${st.note ? ` (${st.note})` : ''}`;
      if (!isIdentity(T)) s += `${st ? '\n   ' : ''} moved (${mv}) cm, yaw ${yawDeg(T).toFixed(2)}°`;
      else if (!st) s += 'not moved';
      lines.push(s);
    }
    if (this.lastMs) lines.push(`last run ${(this.lastMs / 1000).toFixed(1)} s`);
    this.$('aout').textContent = lines.join('\n');
    (this.$('aundo') as HTMLButtonElement).disabled = !this.undo;
  }

  exportFile() {
    const s = this.v.scene, clouds: Record<string, unknown> = {};
    for (const c of this.v.clouds) {
      const T = this.get(c), st = this.stats.get(c.meta.id);
      clouds[c.meta.id] = {
        matrix: rows4(T).map(r => r.map(x => +x.toFixed(9))),
        yaw_deg: +yawDeg(T).toFixed(4), move_cm: this.move(c).map(x => +(x * 100).toFixed(2)),
        ...(st && { rms_before_cm: +(st.rms0 * 100).toFixed(2), rms_after_cm: +(st.rms1 * 100).toFixed(2), boxes: st.boxes, points: st.points, free: st.free, note: st.note }),
      };
    }
    const file = {
      version: 1, kind: 'cloudview-placements', scene: s.name, frame: Object.keys(s.frames)[0], reference: this.ref.meta.id,
      note: 'matrix: row-major 4x4 applied on top of the published cloud, in the scene frame (p_new = M p)',
      volumes: this.alignVols().map(v => v.id), clouds,
    };
    download('placements-viewer.json', [JSON.stringify(file, null, 1) + '\n'], 'application/json');
    return file;
  }
}

